import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { 
  User, FileText, Building2, MessageSquare, Briefcase, 
  Bell, Search,
  CheckCircle2, Clock, Plus, Rocket, Crown, ArrowRight, 
  Shield, Star, Eye, Zap, ChevronRight, Loader2, Globe,
  FileCheck, AlertCircle, BadgeCheck
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import DashboardLayout from '../../components/DashboardLayout';
import { toast } from 'sonner';

import API from '../../constants/api'
// API base via getApiBase()

const getErrorMessage = (error, fallback) => {
  if (error?.details?.length) {
    return error.details.map((item) => item.message).join(' · ');
  }
  return error?.message || fallback;
};

export default function Dashboard() {
  const { user, token, hasModule, activatePro, isProView: isProViewFn } = useAuth();
  const [recentActivity, setRecentActivity] = useState([]);
  const [activatingPro, setActivatingPro] = useState(false);

  const isPro = user?.account_type === 'pro';
  const isProActive = isPro && user?.pro_status === 'actif';
  const isProSuspended = user?.proStatus === 'SUSPENDED';
  const isKycValidated = user?.kyc_status === 'valide';
  const isKycPending = user?.kyc_status === 'en_attente' || user?.kyc_status === 'en_attente_validation';
  const isProView = isProViewFn();

  const handleActivatePro = async () => {
    setActivatingPro(true);
    try {
      await activatePro();
      toast.success('Votre espace Pro est activé. Bienvenue !');
    } catch (error) {
      toast.error(getErrorMessage(error, "Impossible d'activer l'espace Pro"));
      throw error;
    } finally {
      setActivatingPro(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const notifRes = await axios.get(`${API}/notifications`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const payload = notifRes.data?.data ?? notifRes.data;
        const list = payload?.notifications ?? [];
        setRecentActivity(
          Array.isArray(list)
            ? list.slice(0, 5).map((notif) => ({
                ...notif,
                message: notif.body ?? notif.message ?? '',
                created_at: notif.createdAt ?? notif.created_at,
              }))
            : []
        );
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    if (token) fetchData();
  }, [token]);

  const particulierQuickActions = [
    {
      label: 'Publier une demande',
      description: 'Décrivez votre besoin',
      icon: Plus,
      path: '/deposer-demande',
      color: 'bg-blue-500',
    },
    {
      label: 'Mes demandes',
      description: 'Suivre vos demandes publiées',
      icon: FileText,
      path: '/tableau-de-bord/mes-demandes',
      color: 'bg-orange-500',
    },
    {
      label: 'Trouver un artisan',
      description: 'Parcourir l\'annuaire',
      icon: Search,
      path: '/artisans',
      color: 'bg-emerald-500',
    },
    {
      label: 'Offres d\'emploi',
      description: 'Consulter les offres',
      icon: Briefcase,
      path: '/emplois',
      color: 'bg-purple-500',
    },
    {
      label: 'Mes candidatures',
      description: 'Suivre vos réponses',
      icon: FileCheck,
      path: '/tableau-de-bord/mes-candidatures',
      color: 'bg-slate-600',
    },
  ];

  const proQuickActions = [
    {
      label: 'Répondre aux demandes',
      description: 'Consulter les demandes clients',
      icon: Eye,
      path: '/tableau-de-bord/repondre-demandes',
      color: 'bg-amber-500',
      module: 'request_response_access',
    },
    {
      label: 'Ma One Page',
      description: 'Votre page publique pro',
      icon: Globe,
      path: '/tableau-de-bord/ma-page-pro',
      color: 'bg-blue-500',
    },
    {
      label: 'Ma fiche artisan',
      description: 'Profil dans l\'annuaire',
      icon: Building2,
      path: '/tableau-de-bord/ma-fiche-artisan',
      color: 'bg-emerald-500',
      module: 'artisan_profile_access',
    },
    {
      label: 'Publier une offre',
      description: 'Recruter un artisan',
      icon: Briefcase,
      path: '/tableau-de-bord/mes-documents?tab=offres',
      color: 'bg-purple-600',
      module: 'job_offer_publisher',
    },
    {
      label: 'Messagerie pro',
      description: 'Vos conversations',
      icon: MessageSquare,
      path: '/tableau-de-bord/messagerie',
      color: 'bg-slate-600',
    },
  ];

  const getQuickActions = () => {
    if (isProView) {
      return proQuickActions.filter((action) => !action.module || hasModule(action.module)).slice(0, 4);
    }
    return particulierQuickActions;
  };

  const particulierModules = [
    { key: 'basic_access', label: 'Accès de base', icon: CheckCircle2 },
    { key: 'request_publisher', label: 'Publier des demandes', icon: FileText },
  ];

  const proModules = [
    { key: 'artisan_profile_access', label: 'Fiche artisan', icon: Building2 },
    { key: 'artisan_directory_visibility', label: 'Visibilité annuaire', icon: Eye },
    { key: 'request_response_access', label: 'Répondre aux demandes', icon: MessageSquare },
    { key: 'job_offer_publisher', label: 'Publier des offres d\'emploi', icon: Briefcase },
    { key: 'kyc_submission_access', label: 'Soumission KYC', icon: Shield },
    { key: 'verified_badge_access', label: 'Badge vérifié', icon: Star },
  ];

  const getModulesStatus = () => {
    const modules = isProView ? [...particulierModules, ...proModules] : particulierModules;
    return modules.map((mod) => ({
      ...mod,
      active: hasModule(mod.key),
    }));
  };

  return (
    <DashboardLayout>
      <div className="w-full">
            {/* Welcome Header */}
            <div className="mb-8">
              <p className="text-sm font-medium text-[hsl(var(--primary))] mb-2">
                {isProView ? 'Espace professionnel' : 'Espace personnel'}
              </p>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-1">
                Bonjour{user?.prenom ? `, ${user.prenom}` : ''} !
              </h1>
              <p className="text-slate-600">
                {isProView
                  ? 'Gérez votre activité professionnelle depuis votre espace Click&Pro'
                  : 'Bienvenue sur votre espace personnel Click&Pro'
                }
              </p>
            </div>

            {isProSuspended && (
              <div
                className="mb-8 rounded-xl border border-red-200 bg-red-50 p-6 flex items-start gap-3"
                data-testid="dashboard-pro-suspended-alert"
              >
                <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-900">Espace Pro suspendu</p>
                  <p className="text-sm text-red-700 mt-1">
                    Votre accès professionnel a été suspendu. Contactez le support Click&Pro pour plus d&apos;informations.
                  </p>
                </div>
              </div>
            )}

            {/* Account Type Banner */}
            {!isProActive && !isProSuspended && (
              <div className="mb-8 bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--primary-hover))] rounded-2xl p-6 text-white relative overflow-hidden">
                <div className="absolute right-0 top-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="absolute right-20 bottom-0 w-32 h-32 bg-white/10 rounded-full translate-y-1/2" />
                <div className="relative flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Rocket className="w-6 h-6" />
                      <span className="font-bold text-lg">Passez en compte Pro !</span>
                    </div>
                    <p className="text-white/90 max-w-lg">
                      Débloquez toutes les fonctionnalités : fiche artisan, réponses aux demandes, offres d'emploi et bien plus.
                    </p>
                  </div>
                  <Button
                    className="bg-white text-[hsl(var(--primary))] hover:bg-white/90 font-semibold"
                    data-testid="overview-banner-activate-pro"
                    onClick={handleActivatePro}
                    disabled={activatingPro}
                  >
                    {activatingPro ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Activation…
                      </>
                    ) : (
                      <>
                        Activer l'espace Pro
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {isProActive && isProView && (
              <div className="mb-8 lux-dashboard-card-accent p-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <Crown className="w-5 h-5 text-[hsl(var(--primary))]" />
                    <div>
                      <p className="font-semibold text-slate-900">Mode professionnel actif</p>
                      <p className="text-sm text-slate-600">
                        Vous consultez votre espace Pro. Changez de mode depuis Mon profil.
                      </p>
                    </div>
                  </div>
                  <Link to="/tableau-de-bord/profil">
                    <Button variant="outline" size="sm" className="shrink-0">
                      Changer de mode
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              </div>
            )}

            {isProActive && !isProView && (
              <div className="mb-8 lux-dashboard-card p-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-slate-600" />
                    <div>
                      <p className="font-semibold text-slate-900">Mode particulier actif</p>
                      <p className="text-sm text-slate-600">
                        Vous consultez votre espace client. Passez en mode Pro depuis Mon profil.
                      </p>
                    </div>
                  </div>
                  <Link to="/tableau-de-bord/profil">
                    <Button variant="outline" size="sm" className="shrink-0">
                      Changer de mode
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              </div>
            )}

            {/* KYC Banner for Pro users */}
            {isProView && isProActive && !isKycValidated && (
              <div className="mb-8 bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-6 text-white">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <Shield className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">Complétez votre vérification KYC</h3>
                      <p className="text-white/90">Obtenez le badge "Pro vérifié" et gagnez en visibilité</p>
                    </div>
                  </div>
                  <Link to="/tableau-de-bord/kyc">
                    <Button className="bg-white text-amber-600 hover:bg-white/90 font-semibold">
                      Compléter mon KYC
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-slate-900 mb-1">Actions rapides</h2>
              <p className="text-sm text-slate-500 mb-4">
                {isProView
                  ? 'Accédez rapidement à vos outils professionnels'
                  : 'Accédez rapidement à vos services client'}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                {getQuickActions().map((action, i) => (
                  <Link
                    key={i}
                    to={action.path}
                    className="lux-dashboard-card-interactive p-4 group"
                  >
                    <div className={`w-10 h-10 ${action.color} rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                      <action.icon className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-semibold text-slate-900 text-sm">{action.label}</h3>
                    <p className="text-xs text-slate-500 mt-0.5">{action.description}</p>
                  </Link>
                ))}
              </div>
            </div>

            {/* Stats & Modules Grid */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* Account Status */}
              <div className="lux-dashboard-card p-6">
                <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-[hsl(var(--primary))]" />
                  Mon compte
                </h2>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-600">Type de compte</span>
                    <Badge className={isProView ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-800'}>
                      {isProView ? 'Professionnel' : 'Particulier'}
                    </Badge>
                  </div>

                  {isProView && (
                    <>
                      <div className="flex items-center justify-between py-2 border-b border-slate-100">
                        <span className="text-slate-600">Espace Pro</span>
                        <Badge className={isProActive ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-800'}>
                          {isProActive ? 'Actif' : isProSuspended ? 'Suspendu' : 'Non activé'}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between py-2 border-b border-slate-100">
                        <span className="text-slate-600">Badge vérifié</span>
                        <Badge className={isKycValidated ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                          {isKycValidated ? (
                            <span className="flex items-center gap-1"><BadgeCheck className="w-3 h-3" /> Vérifié</span>
                          ) : isKycPending ? 'En revue' : 'Non demandé'}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between py-2 border-b border-slate-100">
                        <span className="text-slate-600">Abonnement</span>
                        <Badge className={user?.subscription_status === 'actif' ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-800'}>
                          {user?.subscription_status === 'actif' ? 'Actif' : 'Inactif'}
                        </Badge>
                      </div>
                    </>
                  )}
                  
                  <div className="flex items-center justify-between py-2">
                    <span className="text-slate-600">Profil complété</span>
                    <Badge className={user?.profile_completed ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                      {user?.profile_completed ? 'Oui' : 'À compléter'}
                    </Badge>
                  </div>
                </div>
                
                <Link to="/tableau-de-bord/profil" className="mt-4 block">
                  <Button variant="outline" className="w-full">
                    Modifier mon profil
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>

              {/* Modules Status */}
              <div className="lux-dashboard-card p-6">
                <h2 className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-[hsl(var(--primary))]" />
                  {isProView ? 'Mes fonctionnalités Pro' : 'Mes fonctionnalités'}
                </h2>
                <p className="text-sm text-slate-500 mb-4">
                  {isProView
                    ? 'Modules disponibles dans votre espace professionnel'
                    : 'Modules disponibles dans votre espace particulier'}
                </p>
                
                <div className="space-y-2">
                  {getModulesStatus().map((mod) => {
                    const Icon = mod.icon;
                    return (
                      <div 
                        key={mod.key}
                        className={`flex items-center gap-3 p-2 rounded-lg ${
                          mod.active ? 'bg-green-50' : 'bg-amber-50'
                        }`}
                      >
                        <Icon className={`w-4 h-4 ${mod.active ? 'text-green-600' : 'text-amber-600'}`} />
                        <span className={`flex-1 text-sm ${mod.active ? 'text-green-800' : 'text-amber-800'}`}>
                          {mod.label}
                        </span>
                        {mod.active ? (
                          <CheckCircle2 className="w-4 h-4 text-green-600" />
                        ) : (
                          <Clock className="w-4 h-4 text-amber-600" />
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {!isProActive && !isProSuspended && (
                  <Link to="/tableau-de-bord/profil" className="mt-4 block">
                    <Button className="w-full btn-clickpro-primary" data-testid="overview-modules-activate-pro">
                      <Rocket className="w-4 h-4 mr-2" />
                      Activer l&apos;espace Pro
                    </Button>
                  </Link>
                )}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="lux-dashboard-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Bell className="w-5 h-5 text-[hsl(var(--primary))]" />
                  Activité récente
                </h2>
                <Link to="/tableau-de-bord/notifications" className="text-sm text-[hsl(var(--primary))] hover:underline">
                  Voir tout
                </Link>
              </div>
              
              {recentActivity.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Bell className="w-12 h-12 mx-auto mb-3 opacity-20" />
                  <p>Aucune activité récente</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentActivity.map((notif, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        notif.type === 'success' ? 'bg-green-100 text-green-600' :
                        notif.type === 'warning' ? 'bg-amber-100 text-amber-600' :
                        'bg-blue-100 text-blue-600'
                      }`}>
                        <Bell className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 text-sm">{notif.title}</p>
                        <p className="text-xs text-slate-500 truncate">{notif.message}</p>
                      </div>
                      <span className="text-xs text-slate-400 whitespace-nowrap">
                        {new Date(notif.created_at).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
      </div>
    </DashboardLayout>
  );
}
